import React, { createContext, useState, useEffect, ReactNode, useContext, useCallback, useRef } from 'react';
import { getCompanyLicenses, CompanyLicenses } from '../api/apiService';
import { useCompanyContext } from './CompanyContext';

interface LicenseContextType {
  licenseInfo: CompanyLicenses | null;
  isLoading: boolean;
  error: string | null;
  refreshLicenses: () => Promise<void>;
}

const LicenseContext = createContext<LicenseContextType | undefined>(undefined);

interface LicenseProviderProps {
  children: ReactNode;
  shouldFetch?: boolean; // Only fetch if user is an owner
}

export const LicenseProvider: React.FC<LicenseProviderProps> = ({ 
  children, 
  shouldFetch = false 
}) => {
  const { selectedCompanyId } = useCompanyContext();
  const selectedCompanyRef = useRef(selectedCompanyId);
  selectedCompanyRef.current = selectedCompanyId;
  const [licenseInfo, setLicenseInfo] = useState<CompanyLicenses | null>(null);
  const [licenseCompanyId, setLicenseCompanyId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const refreshLicenses = useCallback(async () => {
    if (!shouldFetch || !selectedCompanyId) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      // Use selectedCompanyId from context
      const response = await getCompanyLicenses(selectedCompanyId);
      if (selectedCompanyRef.current !== selectedCompanyId) return;
      if (response.ok) {
        setLicenseInfo(response.data);
        setLicenseCompanyId(selectedCompanyId);
      } else if (response.status === 404) {
        // 404 is expected for non-owners, silently ignore
        setLicenseInfo(null);
      } else {
        setError('Failed to fetch licenses');
        setLicenseInfo(null);
      }
    } catch (err) {
      if (selectedCompanyRef.current !== selectedCompanyId) return;
      console.error('Error fetching license info:', err);
      setError('Error fetching licenses');
      setLicenseInfo(null);
    } finally {
      if (selectedCompanyRef.current === selectedCompanyId) {
        setIsLoading(false);
        setHasFetched(true);
      }
    }
  }, [shouldFetch, selectedCompanyId]);

  // Fetch licenses on mount or when selectedCompanyId changes
  useEffect(() => {
    if (shouldFetch && selectedCompanyId) {
      // Reset fetched state when company changes
      setHasFetched(false);
      refreshLicenses();
    }
  }, [shouldFetch, selectedCompanyId, refreshLicenses]);

  return (
    <LicenseContext.Provider value={{
      // Cached capacity may remain visible during a same-company refresh, never
      // during a tenant switch (even before the new request effect has started).
      licenseInfo: shouldFetch && licenseCompanyId === selectedCompanyId ? licenseInfo : null,
      isLoading, error, refreshLicenses,
    }}>
      {children}
    </LicenseContext.Provider>
  );
};

export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (context === undefined) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return context;
};

